import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getSubscriptionDetails,
  getPaymentMethod,
  getPaymentMethodPortal,
  listReceipts,
  downloadInvoiceFile,
  getReceipt,
  validatePromoCode,
  applyPromoCode,
  cancelSubscription
} from '../api';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';

export const useSubscriptionDetails = () => {
  const { email } = useUser();

  return useQuery({
    queryKey: ['subscription', email],
    queryFn: () => getSubscriptionDetails(email),
    enabled: !!email,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePaymentMethod = () => {
  const { email } = useUser();

  return useQuery({
    queryKey: ['paymentMethod', email],
    queryFn: () => getPaymentMethod(email),
    enabled: !!email,
  });
};

export const usePaymentMethodPortal = () => {
  const { email } = useUser();

  return useMutation({
    // `tab` is a blank tab opened inside the click handler, so the user gesture is kept
    // and popup blockers allow it. The portal has no link back, hence the separate tab.
    mutationFn: async ({ tab } = {}) => {
      try {
        const data = await getPaymentMethodPortal(email);
        const url = data?.management_url;
        if (typeof url !== 'string' || !url.startsWith('https://')) {
          throw new Error('No management URL returned');
        }
        // Single-use credential: kept in this scope only, never in mutation state.
        if (!tab) {
          window.location.assign(url); // popup blocked: same-tab fallback
          return;
        }
        if (tab.closed) throw new Error('Portal tab was closed');
        tab.location.replace(url);
      } catch (err) {
        if (tab && !tab.closed) tab.close(); // runs even if Billing unmounted meanwhile
        throw err;
      }
    },
  });
};

export const useReceipts = (page = 1, pageSize = 10) => {
  const { email } = useUser();

  return useQuery({
    queryKey: ['receipts', email, page, pageSize],
    queryFn: () => listReceipts(email, page, pageSize),
    enabled: !!email,
    staleTime: 1000 * 60 * 5,
  });
};

export const useDownloadInvoice = () => {
  return useMutation({
    mutationFn: ({ invoiceId, kind }) => downloadInvoiceFile(invoiceId, kind),
  });
};

export const useGetReceipt = () => {
  return useMutation({
    mutationFn: (transactionId) => getReceipt(transactionId),
  });
};

export const useValidatePromoCode = () => {
  const { email } = useUser();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (code) => validatePromoCode(email, code, i18n.language),
  });
};

export const useApplyPromoCode = () => {
  const { email } = useUser();
  const { refetchSubscription } = useUser();
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: (code) => applyPromoCode(email, code, i18n.language),
    onSuccess: () => {
      refetchSubscription();
    },
  });
};

export const useCancelSubscription = () => {
  const { email } = useUser();
  const { refetchSubscription, refetchUserDetails } = useUser();

  return useMutation({
    mutationFn: (options) => cancelSubscription(email, options?.target),
    onSuccess: () => {
      // Refetch subscription and user details after cancellation
      refetchSubscription();
      refetchUserDetails();
    },
  });
};
